"use strict";

import { Avatar, Box, IconButton, Slider, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import DownloadIcon from "@mui/icons-material/Download";

import React, { useEffect, useRef, useState } from "react";

import { playExclusively } from "./exclusiveAudio.js";

const getPreviewAudioSrc = (preview) => {
  if (!preview?.audio_base_64) return "";

  const mediaType = preview.media_type || "audio/mpeg";
  return `data:${mediaType};base64,${preview.audio_base_64}`;
};

const downloadBase64Audio = (preview, characterName, index) => {
  const audioSrc = getPreviewAudioSrc(preview);
  if (!audioSrc) return;

  const extension = preview.media_type?.includes("wav") ? "wav" : "mp3";
  const safeName = characterName
    ? characterName.replace(/[^\w-]+/g, "_")
    : `voice_preview_${index + 1}`;

  const link = document.createElement("a");
  link.href = audioSrc;
  link.download = `${safeName}.${extension}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const VoicePreviewRow = ({
  preview,
  characterName,
  index,
  speakerId,
  emotion,
  isAccepted,
  isAccepting,
  onAccept,
}) => {
  const audioRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const displayName = characterName || speakerId || `Voice ${index + 1}`;
  const audioSrc = getPreviewAudioSrc(preview);

  useEffect(() => {
    setIsPlaying(false);
    setProgress(0);
  }, [audioSrc]);

  const handlePlayPause = async () => {
    if (!audioRef.current || !audioSrc) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    try {
      await playExclusively(audioRef.current);
      setIsPlaying(true);
    } catch (error) {
      console.error("Failed to play audio preview:", error);
    }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;

    const currentTime = audioRef.current.currentTime || 0;
    const duration = audioRef.current.duration || preview.duration_secs || 0;

    if (!duration) {
      setProgress(0);
      return;
    }

    setProgress((currentTime / duration) * 100);
  };

  const handleSeek = (event, value) => {
    if (!audioRef.current) return;

    const duration = audioRef.current.duration || preview.duration_secs || 0;
    audioRef.current.currentTime = (Number(value) / 100) * duration;
    setProgress(Number(value));
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "32px 32px 1fr auto",
          sm: "36px 36px 1fr auto",
        },
        alignItems: "center",
        gap: 1.25,
        px: 2,
        py: 1.5,
        borderTop: index === 0 ? "none" : "1px solid #e5e7eb",
      }}
    >
      <Avatar
        sx={{
          width: 26,
          height: 26,
          fontSize: 13,
          bgcolor: "#d7f3ff",
          color: "#0f172a",
        }}
      >
        {displayName.charAt(0).toUpperCase()}
      </Avatar>

      <IconButton
        aria-label={`${isPlaying ? "Pause" : "Play"} preview ${index + 1}`}
        onClick={handlePlayPause}
        disabled={!audioSrc}
        size="small"
        sx={{ color: "#0f172a" }}
      >
        {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
      </IconButton>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Slider
          size="small"
          value={progress}
          onChange={handleSeek}
          disabled={!audioSrc}
          sx={{
            color: "#111827",
            "& .MuiSlider-thumb": {
              width: 16,
              height: 16,
            },
            "& .MuiSlider-track": {
              height: 4,
            },
            "& .MuiSlider-rail": {
              height: 4,
              color: "#d1d5db",
              opacity: 1,
            },
          }}
        />

        <Typography
          variant="caption"
          sx={{
            minWidth: 44,
            color: "#6b7280",
            display: { xs: "none", sm: "block" },
          }}
        >
          {preview.duration_secs ? `${preview.duration_secs.toFixed(1)}s` : ""}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <IconButton
          size="small"
          aria-label={isAccepted ? "Voice added" : "Add voice to character"}
          onClick={() =>
            onAccept({
              generatedVoiceId: preview.generated_voice_id,
              speakerId,
              emotion,
            })
          }
          disabled={!preview.generated_voice_id || isAccepted || isAccepting}
        >
          {isAccepted ? (
            <CheckIcon fontSize="small" />
          ) : (
            <AddIcon fontSize="small" />
          )}
        </IconButton>

        <IconButton
          size="small"
          aria-label="Download preview"
          onClick={() => downloadBase64Audio(preview, displayName, index)}
          disabled={!audioSrc}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Box>

      <audio
        ref={audioRef}
        src={audioSrc}
        onTimeUpdate={handleTimeUpdate}
        // Also covers being paused by another preview starting.
        onPause={() => setIsPlaying(false)}
        onEnded={handleEnded}
        style={{ display: "none" }}
      />
    </Box>
  );
};

export default VoicePreviewRow;
